// 치과용 「환자 앱 등록 처리위탁 확인」 카드 (덴클 케어 1차 W1, 2026-10-04 계획_환자앱1차_통합 §2-3 · 정본 v2.5a pa-clinic-1.1(10-05 교체) → 정본 10-06 §2 pa-clinic-1.2(10-06 첫 게시 판 교체) · L-1)
// 구조는 DeskClinicConsentCard 복제: 서버가 알려 준 판·해시(current·currentTextHash)와 웹 원문(config/careLegal.js CARE_PA_CLINIC)이
//   같을 때만 확인을 보낸다. 원문은 바이트 그대로 pre-wrap. 마지막 줄 '☐'는 해시 대상이라 그대로 두고 실제 체크박스는 아래에 따로.
// 대표만 확인·철회. 직원은 "대표 계정만 확인할 수 있습니다."(정본 L-1).
// 철회: [처리위탁 확인 철회] → 확인 창(정본 v2 §9 L-8) → revokePatientAppClinicConsent. 철회 후 7일간 L-9 표시 + [다시 확인](정본 §2-5).
// 노출: 호출자가 isCareAppOn(config/features.careApp, strict) 일 때만 마운트 + 서버 응답 isPilot === true 일 때만 그림(아래 가드).
//   원문이 TODO(정본 v2 미확정)면 서버를 부르지 않으므로 파일럿 여부를 몰라 숨김.
// 서버 함수 이름·응답 = services/careAppApi.js 가정(가칭). 콘솔에는 오류 코드만.
import React, { useCallback, useEffect, useState } from 'react';
import { CARE_PA_CLINIC, isCareTextReady, paClinicText, verifyCareText } from '../../config/careLegal';
import { getPatientAppClinicConsentShared, invalidatePatientAppClinicConsent, acceptPatientAppClinicConsent, revokePatientAppClinicConsent, careClinicError } from '../../services/careAppApi';

function fmtMonthDay(ms) {
    if (!Number.isFinite(Number(ms))) return '';
    const k = new Date(Number(ms) + 9 * 3600 * 1000);
    return `${k.getUTCMonth() + 1}월 ${k.getUTCDate()}일`;
}

// 정본 v2.6 §2-1 L-8 (철회 확인 창, 법적보안 10-05 — pa-clinic-1.1 9항과 문장 맞춤. 종전 "환자 앱에서 우리 치과가 보이지 않게 됩니다"는 사실과 달라 교체)
export const REVOKE_TEXT = '철회하면 새 앱 등록·의뢰서 연결·환자 알림이 바로 멈추고, 앱의 치과 찾기 결과에서 우리 치과가 바로 빠지며, 데스크 접수 완료 화면의 앱 안내도 더 이상 표시되지 않습니다. 이미 등록한 환자의 앱에서는 우리 치과의 보철물 제작 기록이 바로 보이지 않게 되고, 삭제될 때까지 삭제 예정 안내와 등록 삭제 요청을 위한 우리 치과 이름만 표시됩니다. 앱 등록 정보와 제작 기록은 7일 뒤 모두 삭제되며, 7일 안에 다시 확인하면 삭제되지 않습니다.';
// 1.0 은 신규·재확인 모두 서버가 거부(10-05 patient-app-gate.js acceptPatientAppClinicConsent consent-mismatch). 웹도 1.0 으로는 확인을 보내지 않는다 — 재확인은 현재 판(CARE_PA_CLINIC = 1.2, 10-06)으로만.
// 1.1 확인 치과(서버 isCurrent:false)는 아래 needReconfirm 경로로 1.2 본문 + '확인 내용이 바뀌었습니다…' + [다시 확인] 을 본다(1.0 전용 L-1 문구는 wasV10 에만).
const LEGACY_V10 = 'pa-clinic-1.0';
// 확인 완료 줄 표시 여부(10-08 끔 — 설정 › 환자 정보 처리위탁으로 이동)
const SHOW_CONFIRMED_LINE = false;

function fmtDate(ms) {
    if (!Number.isFinite(Number(ms))) return '';
    const k = new Date(Number(ms) + 9 * 3600 * 1000);
    return `${k.getUTCFullYear()}.${k.getUTCMonth() + 1}.${k.getUTCDate()}`;
}

const TITLE = '환자 앱 등록 처리위탁 확인';
// 정본 v2.4-4 #6 L-1 재확인 안내 (해시 대상 아님 — 1.0 확인 치과 대표 계정에만)
const RECONFIRM_V10_TEXT = '확인 내용이 바뀌었습니다(앱의 치과 찾기 표시, 접수 완료 화면 앱 안내 추가). 다시 확인해야 앱의 치과 찾기에 우리 치과가 나옵니다. 다시 확인하기 전에도 데스크 QR 앱 등록은 그대로 됩니다.';

function CareAppClinicConsentCard() {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const textReady = isCareTextReady(CARE_PA_CLINIC);
    const [verified, setVerified] = useState(null);     // null=대조 중 / true / false
    const [st, setSt] = useState({ loading: true, data: null, err: '' });
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const [revokeStep, setRevokeStep] = useState(false);

    // 웹 원문 자체 해시 대조 — 정본과 바이트가 다르면 보이지 않는다
    useEffect(() => {
        let alive = true;
        if (!textReady) { setVerified(false); return undefined; }
        verifyCareText(CARE_PA_CLINIC, paClinicText(CARE_PA_CLINIC)).then(ok => { if (alive) setVerified(ok); });
        return () => { alive = false; };
    }, [textReady]);

    // 조회는 공유 캐시(getPatientAppClinicConsentShared) — 같은 화면의 useRoadmapPilot 훅과 네트워크 1회 공유 (2026-10-06).
    //   force=true(다시 불러오기·확인·철회 뒤)는 캐시를 비우고 새로 받으며, 훅 구독자에게도 재판정을 알린다.
    const load = useCallback(async (force = false) => {
        setSt(s => ({ ...s, loading: true, err: '' }));
        try {
            if (force === true) invalidatePatientAppClinicConsent();
            const r = await getPatientAppClinicConsentShared({ force: force === true });
            setSt({ loading: false, data: r, err: '' });
        } catch (e) {
            setSt({ loading: false, data: null, err: careClinicError(e, '처리위탁 확인 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        }
    }, []);

    useEffect(() => { if (verified === true) load(); }, [verified, load]);

    const d = st.data || {};
    // 서버 현재 판과 웹 원문이 같은 판·해시인가
    const sameVersion = !!st.data && d.current === CARE_PA_CLINIC.version && String(d.currentTextHash || '').toLowerCase() === String(CARE_PA_CLINIC.textHash).toLowerCase();

    const accept = async () => {
        if (busy || !checked || !d.canConfirm || !sameVersion || CARE_PA_CLINIC.version === LEGACY_V10) return;
        setBusy(true); setNotice('');
        try {
            const r = await acceptPatientAppClinicConsent(CARE_PA_CLINIC.version, CARE_PA_CLINIC.textHash);
            if (r?.ok !== true) {
                console.error('[환자 앱 위탁 확인] 응답 이상: ok=', r?.ok);
                setNotice('확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
                return;
            }
            setChecked(false);
            await load(true);
        } catch (e) {
            setNotice(careClinicError(e, '확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.'));
        } finally { setBusy(false); }
    };

    const revoke = async () => {
        if (busy || !d.canConfirm) return;
        setBusy(true); setNotice('');
        try {
            const r = await revokePatientAppClinicConsent();
            if (r?.ok !== true) {
                console.error('[환자 앱 위탁 확인] 철회 응답 이상: ok=', r?.ok);
                setNotice('철회를 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
                return;
            }
            setRevokeStep(false);
            await load(true);
        } catch (e) {
            setNotice(careClinicError(e, '철회를 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.'));
        } finally { setBusy(false); }
    };

    // 파일럿 치과만 노출(2026-10-04 검증 결과 4번): 서버 getPatientAppClinicConsent 응답의 isPilot === true 일 때만 카드를 그린다.
    //   fail-closed — 응답 전·조회 실패·문안 미확정·구 서버(isPilot 키 없음)는 모두 숨김. 조회 실패는 careClinicError 가 콘솔에 기록한다.
    //   (파일럿 여부를 알기 전에는 "준비 중"·오류 상자도 보이지 않는다 — 비파일럿 치과에 기능 존재를 드러내지 않기 위해)
    if (!(st.data && st.data.isPilot === true)) return null;

    // 문안 미확정(TODO) 또는 해시 불일치 — 원문도 서버 호출도 없이 "준비 중"
    if (verified === false) {
        return (
            <div style={C.box}>
                <div style={C.title}>{TITLE}</div>
                <div style={C.sub}>준비 중입니다. 확인 내용이 확정되면 이 자리에서 치과 대표가 확인할 수 있습니다.</div>
            </div>
        );
    }
    // 서버 정본 표가 비어 있음(ready:false) — 확인 불가
    if (!st.loading && !st.err && st.data && st.data.ready === false) {
        return (
            <div style={C.box}>
                <div style={C.title}>{TITLE}</div>
                <div style={C.sub}>확인 문서가 아직 준비되지 않았습니다. 준비되면 이 자리에서 치과 대표가 확인할 수 있습니다.</div>
            </div>
        );
    }
    // 조회 중에는 아무것도 그리지 않는다(10-08 — 확인 끝난 치과에 카드가 잠깐 비치지 않게)
    if (verified === null || st.loading) return null;

    if (st.err) {
        return (
            <div style={C.box}>
                <div style={C.title}>{TITLE}</div>
                <div style={C.errNote}>{st.err}</div>
                <button type="button" onClick={() => load(true)} style={{ ...C.btnS, marginTop: 8 }}>다시 불러오기</button>
            </div>
        );
    }

    // 철회 확인 창(L-8) — 현재 판 확인 완료 상태와 1.0 확인 상태(재확인 필요) 공용
    const revokeBox = revokeStep && (
        <div style={C.revokeBox}>
            <div>{REVOKE_TEXT}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button type="button" disabled={busy} onClick={revoke} style={{ ...C.btnDanger, opacity: busy ? 0.5 : 1 }}>{busy ? '기록 중…' : '철회'}</button>
                <button type="button" disabled={busy} onClick={() => setRevokeStep(false)} style={C.btnS}>취소</button>
            </div>
        </div>
    );

    // 확인 완료 — 2026-10-08 대표 "권고대로": 확인 끝난 치과에는 아무것도 그리지 않는다(상태·철회는 설정 › 환자 정보 처리위탁 한 곳으로).
    //   아래 확인 완료 줄·철회 블록은 보존(SHOW_CONFIRMED_LINE=false) — 되돌릴 때 상수만 바꾼다.
    if (d.confirmed && d.isCurrent && !SHOW_CONFIRMED_LINE) return null;
    if (d.confirmed && d.isCurrent) {
        return (
            <div style={C.okBox}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ flex: 1, minWidth: 200 }}>{`${TITLE} 완료${fmtDate(d.at) ? ` (${fmtDate(d.at)}, ${d.version || ''})` : ''}`}</span>
                    {d.canConfirm && !revokeStep && (
                        <button type="button" onClick={() => { setRevokeStep(true); setNotice(''); }} style={C.btnRevoke}>처리위탁 확인 철회</button>
                    )}
                </div>
                {revokeBox}
                {notice && <div style={C.errNote}>{notice}</div>}
            </div>
        );
    }

    const needReconfirm = d.confirmed && !d.isCurrent;
    // 정본 v2.4-4 #6 L-1 — 1.0 확인 기록이 있는 치과(서버 version === 'pa-clinic-1.0', isCurrent:false)의 대표 계정에만. 직원은 기존 안내 + '대표 계정만…'
    const wasV10 = d.version === 'pa-clinic-1.0';
    // 정본 v2.6 §2-2 L-9 — 철회 후(삭제 예정일 전) 표시. 삭제 예정일이 지났거나 모르면 날짜 없이(현행 유지)
    const purgePending = d.revoked && Number.isFinite(Number(d.purgeAt)) && Number(d.purgeAt) > Date.now();
    const sub = d.revoked
        ? (purgePending ? `처리위탁 확인을 철회했습니다. 앱 등록 정보와 제작 기록은 ${fmtMonthDay(d.purgeAt)}에 삭제됩니다. 그 전에 [다시 확인]을 누르면 삭제되지 않습니다.` : '처리위탁 확인을 철회했습니다.')
        : needReconfirm
            ? (d.canConfirm && wasV10 ? RECONFIRM_V10_TEXT : '확인 내용이 바뀌었습니다. 바뀐 내용을 읽고 다시 확인해 주세요.')
            : '치과 대표가 아래 내용을 확인해야 환자가 앱으로 우리 치과를 등록할 수 있습니다.';

    return (
        <div style={C.box}>
            <div style={C.title}>{TITLE}</div>
            <div style={C.sub}>{sub}</div>
            <div style={C.text}>{paClinicText(CARE_PA_CLINIC)}</div>
            {!sameVersion ? (
                <div style={C.errNote}>확인 내용이 최신이 아닙니다. 화면을 새로고침해 주세요.</div>
            ) : d.canConfirm ? (
                <>
                    <div style={C.agreeHint}>위 글 마지막 줄의 네모(☐)는 문안의 일부입니다. 확인은 아래 칸을 눌러 체크해 주세요.</div>
                    <label style={{ ...C.agree, ...(checked ? C.agreeOn : null) }}>
                        <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ width: 20, height: 20, flexShrink: 0, margin: 0, accentColor: '#0f6b5c' }} />
                        <span style={{ fontWeight: 700 }}>위 내용을 확인했습니다(체크)</span>
                    </label>
                    <button type="button" disabled={!checked || busy} onClick={accept} style={{ ...C.btnP, opacity: !checked || busy ? 0.5 : 1 }}>
                        {busy ? '기록 중…' : (needReconfirm || d.revoked ? '다시 확인' : '확인')}
                    </button>
                </>
            ) : (
                <div style={C.readonly}>대표 계정만 확인할 수 있습니다.</div>
            )}
            {/* 1.0 확인 치과(재확인 필요 — 1.0 기록은 서버에서 계속 유효)도 대표는 언제든 철회 가능(pa-clinic-1.0 9항). 철회 후 재확인은 위 [다시 확인] = 1.1 로만 */}
            {needReconfirm && !d.revoked && d.canConfirm && !revokeStep && (
                <div style={{ marginTop: 10 }}>
                    <button type="button" onClick={() => { setRevokeStep(true); setNotice(''); }} style={C.btnRevoke}>처리위탁 확인 철회</button>
                </div>
            )}
            {needReconfirm && !d.revoked && d.canConfirm && revokeBox}
            {notice && <div style={C.errNote}>{notice}</div>}
        </div>
    );
}

const C = {
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    okBox: { fontSize: 12.5, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 11px', marginBottom: 14 },
    title: { fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 },
    sub: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6 },
    text: { marginTop: 10, fontSize: 13, color: '#1e293b', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', maxHeight: 340, overflowY: 'auto' },
    agreeHint: { marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.5 },
    agree: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: '#0f172a', marginTop: 6, cursor: 'pointer', background: '#f0fdf9', border: '2px solid #0f6b5c', borderRadius: 10, padding: '11px 14px' },
    agreeOn: { background: '#d1fae5' },
    btnP: { marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#0f6b5c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    btnS: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
    btnRevoke: { padding: '4px 10px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    btnDanger: { padding: '6px 14px', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
    revokeBox: { marginTop: 8, fontSize: 12.5, color: '#7f1d1d', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', lineHeight: 1.6 },
    readonly: { marginTop: 10, fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px' },
    errNote: { marginTop: 10, fontSize: 12.5, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '8px 11px', lineHeight: 1.55 },
};

export default CareAppClinicConsentCard;
